import { useState, useRef, useCallback, useEffect } from "react";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { getAgentSessionId } from "./agentSession";
import { useVoiceAgentLanguage } from "./useVoiceAgentLanguage";
import { useAgentWebhook } from "./useAgentWebhook";
import type { WriteAgentState } from "./types";

type GetUserFn = () => Promise<{
  data: { user: { id: string } | null };
}>;

export function useWriteAgent(options?: { getUser?: GetUserFn }) {
  const getUser =
    options?.getUser ?? (() => getSupabaseClient().auth.getUser());
  const [state, setState] = useState<WriteAgentState>("idle");
  const [inputText, setInputText] = useState("");
  const [response, setResponse] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const sessionIdRef = useRef(getAgentSessionId());
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [voiceLanguage] = useVoiceAgentLanguage();
  const webhook = useAgentWebhook({ getUser });

  useEffect(() => {
    if (state === "processing") {
      setElapsedSeconds(0);
      timerRef.current = setInterval(() => {
        setElapsedSeconds((s) => s + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [state]);

  const openSheet = useCallback(() => {
    sessionIdRef.current = getAgentSessionId();
    setResponse(null);
    setError(null);
    setState("open");
  }, []);

  const submit = useCallback(async () => {
    if (!inputText.trim()) return;
    setState("processing");

    try {
      const message = await webhook.submit({
        text: inputText,
        sessionId: sessionIdRef.current || getAgentSessionId(),
        language: voiceLanguage,
      });
      setResponse(message);
      setState("response");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Request failed");
      setState("error");
    }
  }, [inputText, webhook, voiceLanguage]);

  /** Clear UI for another message, but keep the Redis conversation session. */
  const continueConversation = useCallback(() => {
    sessionIdRef.current = getAgentSessionId();
    setInputText("");
    setResponse(null);
    setError(null);
    setState("open");
  }, []);

  const reset = useCallback(() => {
    // Keep sessionId so Redis memory survives sheet close / remount.
    setInputText("");
    setResponse(null);
    setError(null);
    setState("idle");
  }, []);

  return {
    state,
    inputText,
    response,
    error,
    elapsedSeconds,
    isSheetOpen: state !== "idle",
    setInputText,
    openSheet,
    submit,
    continueConversation,
    reset,
  };
}
